<template>
  <div>
    <b-tabs content-class="mt-3" v-model="tab">
      <b-tab :title="$dt('detection.parsed_json_data', 'Parsed Json Data')" active>
          <b-form-group v-for="item in editableJsonFields" :key="item.key">
            <label>{{ item.name }}({{ item.type_name }}) ({{ item.lookup_key }})</label>

            <div v-if="(item.type_name === 'Number') || (item.type_name === 'Decimal')" >
              <b-form-input type="number" v-model="parsedJson[item.lookup_key]" />
            </div>
            <div v-else-if="(item.type_name === 'Timestamp')" >
              <DatePicker format="DD.MM.YYYY hh:mm:ss" value-type="timestamp"
              v-model="parsedJson[item.lookup_key]" />
            </div>
            <div v-else-if="['String Array', 'Number Array', 'Decimal Array', 'Timestamp Array', 'Boolean Array', 'String'].includes(item.type_name)" >
              <b-form-input type="text" v-model="parsedJson[item.lookup_key]" />
            </div>
            <div v-else-if="(item.type_name === 'Boolean')" >
              <b-form-checkbox v-model="parsedJson[item.lookup_key]" />
            </div>
            <b-alert v-if="fieldsTabErrors[item.lookup_key]" show variant="danger">
              {{ fieldsTabErrors[item.lookup_key] }}
            </b-alert>
          </b-form-group>
          <b-alert v-for="(error, key) in remainingFieldsTabErorrs" show variant="danger" :key="key">
            {{ key }}
            {{ error }}
          </b-alert>
      </b-tab>
      <b-tab :title="$dt('detection.raw_json_data', 'Raw Json Data')">
        <b-form-textarea
          id="textarea"
          v-model="jsonString"
          rows="20"
          max-rows="20"
          @update="syncRawToParsed"
        ></b-form-textarea>
        <b-alert v-for="(error, key) in errors" show variant="danger" :key="key">
            {{ key }}
            {{ error }}
          </b-alert>
      </b-tab>
    </b-tabs>

  </div>
</template>

<script>
import merge from 'lodash.merge';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import DatePicker from 'vue2-datepicker';

export default {
  name: 'DetectionJson',
  components: {
    DatePicker,
  },
  data() {
    return {
      json: {},
      jsonString: '',
      parsedJson: {},
      tab: 0,
    };
  },
  props: {
    jsonData: {
      type: Object,
      default: () => ({}),
    },
    parsedData: {
      type: Array,
      default: () => ([]),
    },
    jsonFields: {
      type: Array,
      default: () => ([]),
    },
    jsonFieldOptions: {
      type: Object,
      default: () => ({}),
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    parsedJson: {
      handler(val) {
        if (this.tab === 0) {
          this.syncParsedToRaw();
        }
      },
      deep: true,
    },
    json: {
      handler(val) {
        this.$emit('update', val);
      },
      deep: true,
    },
  },
  computed: {
    editableJsonFields() {
      return this.jsonFields.filter((item) => item.available_in_form)
        .map((item) => {
          const newItem = merge({}, item);
          newItem.lookup_key = newItem.lookup_key.replace(/\[\*\]/g, '');
          return newItem;
        });
    },
    fieldsTabErrors() {
      const errors = {};
      this.editableJsonFields.forEach((item) => {
        const key = item.lookup_key;
        if (this.errors[key]) {
          errors[key] = this.errors[key];
        }
      });
      return errors;
    },
    remainingFieldsTabErorrs() {
      const errors = { ...this.errors };
      this.editableJsonFields.forEach((item) => {
        const key = item.lookup_key;
        if (errors[key]) {
          delete errors[key];
        }
      });
      return errors;
    },
  },
  mounted() {
    this.initializeData();
  },
  methods: {
    initializeData() {
      this.json = merge({}, this.jsonData);
      this.jsonString = JSON.stringify(this.json, null, 2);
      // this.readParsedFromJson();
      this.syncRawToParsed();
    },
    isArrayField(typeName) {
      const arrayTypes = ['String Array', 'Number Array', 'Decimal Array', 'Timestamp Array', 'Boolean Array'];
      return arrayTypes.includes(typeName);
    },
    syncParsedToRaw() {
      const parsed = merge({}, this.json);
      // foreach of the array fields, we need to convert the value to raw
      this.editableJsonFields.forEach((item) => {
        const val = this.parsedJson[item.lookup_key];
        this.setProperty(parsed, item.lookup_key, this.valueToRaw(val, item.type_name));
      });
      this.json = parsed;

      this.jsonString = JSON.stringify(this.json, null, 2);
    },
    syncRawToParsed() {
      try {
        this.json = JSON.parse(this.jsonString);
        const newFields = {};
        this.editableJsonFields.forEach((item) => {
          const val = this.getProperty(this.json, item.lookup_key);
          newFields[item.lookup_key] = this.valueFromRaw(val, item.type_name);
        });
        this.parsedJson = newFields;
      } catch (e) {
        console.error(e);
      }
    },
    getProperty(obj, path) {
      const keys = path.split('.');

      return keys.reduce((acc, key) => {
        // eslint-disable-next-line no-prototype-builtins
        if (acc && acc.hasOwnProperty(key)) {
          return acc[key];
        }
        return undefined;
      }, obj);
    },
    setProperty(obj, path, value) {
      const keys = path.split('.');

      keys.reduce((acc, key, index) => {
        if (index === keys.length - 1) {
          // Reached the last key, set the value
          acc[key] = value;
        } else {
          // Check if the key exists, if not, create an empty object
          // eslint-disable-next-line no-prototype-builtins
          if (!acc.hasOwnProperty(key) || typeof acc[key] !== 'object') {
            acc[key] = {};
          }
          // Move to the next nested level
          return acc[key];
        }
      }, obj);
    },
    valueFromRaw(value, itemTypeName) {
      if (this.isArrayField(itemTypeName)) {
        if (value === null || value === undefined) {
          return null;
        }
        const conv = JSON.stringify(value);
        return conv;
      }
      if (value === null || value === undefined) {
        return null;
      }
      // if (itemTypeName === 'Timestamp') {
      //   return parseInt(value * 1000, 10);
      // }
      return value;
    },
    valueToRaw(value, itemTypeName) {
      // quick check for empty values
      if (value === null || value === undefined || value === '') {
        return null;
      }
      if (this.isArrayField(itemTypeName)) {
        try {
          return JSON.parse(value.toString());
        } catch (e) {
          console.error('valueToRaw error', itemTypeName, typeof (value), value, e);
          return null;
        }
      }

      switch (itemTypeName) {
        case 'Number':
        case 'Timestamp':
          return parseInt(value, 10);
        case 'Decimal':
          return parseFloat(value);
        case 'Boolean':
          return !!value;
        case 'String':
          return value.toString();
        default:
          return value;
      }
    },

  }
};
</script>

<style scoped lang="scss"></style>
